'use client';

import { resolveObjectArray } from '@macaron-a2ui/core/v0_8';
import type { RecordAny } from '@macaron-a2ui/core/v0_8';

export interface SelectionRules {
  maxSelection: number;
  requiredSelection: number;
}

export interface SelectionItem {
  value: string;
  child: string;
}

export function resolveSelectionRules(config: RecordAny, itemCount: number): SelectionRules {
  if (itemCount <= 0) return { maxSelection: 0, requiredSelection: 0 };
  const rawMax = typeof config.maxSelection === 'number' ? Math.floor(config.maxSelection) : 1;
  const rawRequired = typeof config.requiredSelection === 'number' ? Math.floor(config.requiredSelection) : 1;
  const maxSelection = Math.max(1, Math.min(rawMax > 0 ? rawMax : 1, itemCount));
  const requiredSelection = Math.max(1, Math.min(rawRequired > 0 ? rawRequired : 1, maxSelection));
  return { maxSelection, requiredSelection };
}

export function normalizeSelection(values: string[], validValues: string[], maxSelection: number): string[] {
  if (maxSelection <= 0 || values.length === 0) return [];
  const validSet = new Set(validValues);
  const deduped: string[] = [];
  for (const value of values) {
    if (!validSet.has(value)) continue;
    if (deduped.includes(value)) continue;
    deduped.push(value);
    if (deduped.length >= maxSelection) break;
  }
  return deduped;
}

export function isSelectionNormalized(rawSelection: unknown[] | null | undefined, normalized: string[]): boolean {
  if (!rawSelection) return normalized.length === 0;
  if (rawSelection.length !== normalized.length) return false;
  return normalized.every((value, index) => rawSelection[index] === value);
}

export function resolveSelectionItems(expr: unknown, data: RecordAny): SelectionItem[] {
  return resolveObjectArray(expr, data)
    .map((item) => {
      const value = typeof item.value === 'string' ? item.value : '';
      const child = typeof item.child === 'string' ? item.child : '';
      return value && child ? { value, child } : null;
    })
    .filter((item): item is SelectionItem => Boolean(item));
}

export function toggleSelectionValue(selected: string[], value: string, maxSelection: number): string[] {
  if (!value || maxSelection <= 0) return selected;
  const next = new Set(selected);
  if (next.has(value)) {
    next.delete(value);
    return Array.from(next);
  }
  if (maxSelection === 1) return [value];
  if (next.size >= maxSelection) return selected;
  next.add(value);
  return Array.from(next);
}
